/**
 * Onboarding Step 3: Confirmation
 *
 * Read-only summary with "Edit" and "Confirm" buttons
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InfoIcon } from "lucide-react";
import { getCreditBalance } from "@/services/api-client";
import { useFeatureFlag } from "@/services/feature-flags";
import { formatBalance } from "@/features/billing/billing-format";
import {
  INDUSTRY_SECTOR_OTHER_CODE,
  isIndustrySectorCode,
} from "@/constants/company-form-options";
import { useLegalFormOptions } from "@/features/onboarding/hooks/useLegalFormOptions";

interface CompanyData {
  business_activity: string;
  company_name: string;
  legal_form: string;
  legal_form_other?: string;
  industry_sector: string[] | string;
  industry_sector_other?: string;
  nip: string;
}

interface Step3ConfirmProps {
  data: CompanyData | null;
}

export function Step3Confirm({ data }: Step3ConfirmProps) {
  const { t } = useTranslation(["onboarding", "common"]);
  const { getLabel: getLegalFormLabel } = useLegalFormOptions();
  const isAdminTopUpMode = useFeatureFlag("BILLING_ADMIN_TOP_UP_ONLY");
  const isPrepaidEnabled = useFeatureFlag("BILLING_PREPAID");
  const showBilling = isAdminTopUpMode || isPrepaidEnabled;

  const [creditBalance, setCreditBalance] = useState<number | null>(null);

  useEffect(() => {
    if (!showBilling) return;

    void (async () => {
      try {
        const response = await getCreditBalance();
        setCreditBalance(response.balance);
      } catch {
        // Billing details are supplementary during onboarding.
      }
    })();
  }, [showBilling]);

  if (!data) {
    return (
      <p className="text-sm text-[var(--muted-foreground)]">{t("onboarding:confirm.noData")}</p>
    );
  }

  const normalizedIndustrySectors = Array.isArray(data.industry_sector)
    ? data.industry_sector
    : (typeof data.industry_sector === "string" && data.industry_sector.trim()
      ? [data.industry_sector.trim()]
      : []);

  const industrySectorLabel = normalizedIndustrySectors.map((sector) => {
    if (sector === INDUSTRY_SECTOR_OTHER_CODE && data.industry_sector_other) {
      return t("onboarding:confirm.industrySectorOtherValue", {
        value: data.industry_sector_other,
      });
    }
    if (isIndustrySectorCode(sector)) {
      return t(`onboarding:step2.industrySector.options.${sector}`);
    }
    return sector;
  }).join(", ");

  const summaryItems = [
    {
      label: t("onboarding:confirm.businessActivity"),
      value: data.business_activity,
      multiline: true,
    },
    {
      label: t("onboarding:confirm.companyName"),
      value: data.company_name,
    },
    {
      label: t("onboarding:confirm.legalForm"),
      value: data.legal_form === "other" && data.legal_form_other
        ? data.legal_form_other
        : getLegalFormLabel(data.legal_form) ?? data.legal_form,
    },
    {
      label: t("onboarding:confirm.industrySector"),
      value: industrySectorLabel || t("common:labels.none"),
    },
    {
      label: t("onboarding:confirm.nip"),
      value: data.nip,
    },
  ];

  return (
    <div className="space-y-5">
      <div className="rounded-[var(--r-lg)] border border-[var(--border)] bg-[color:color-mix(in_srgb,var(--accent)_58%,var(--card))] p-5">
        <p className="text-sm leading-6 text-[var(--foreground)]">
          {t("onboarding:confirm.instructions")}
        </p>
      </div>

      <div className="grid gap-4">
        {summaryItems.map((item) => (
          <div
            key={item.label}
            className="rounded-[var(--r-lg)] border border-[var(--border)] bg-[color:color-mix(in_srgb,var(--background)_88%,transparent)] p-5"
          >
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--muted-foreground)]">
              {item.label}
            </h3>
            <p className={`mt-3 text-sm leading-7 text-[var(--foreground)] ${item.multiline ? "whitespace-pre-wrap" : ""}`}>
              {item.value}
            </p>
          </div>
        ))}
      </div>

      {showBilling && (
        <div className="flex items-start gap-2.5 px-1 text-xs leading-5 text-[var(--muted-foreground)]">
          <InfoIcon
            aria-hidden="true"
            className="mt-0.5 h-4 w-4 shrink-0 text-[var(--muted-foreground)]"
          />
          <div className="space-y-1">
            {creditBalance !== null && (
              <p className="font-medium text-[var(--foreground)]">
                {t("onboarding:confirm.disclaimer.balance", {
                  balance: formatBalance(creditBalance),
                })}
              </p>
            )}
            <p>{t("onboarding:confirm.disclaimer.creditRule")}</p>
            <p>
              {t("onboarding:confirm.disclaimer.contact", {
                email: "r@kaluznalegal.pl",
              })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
